//import FormError from '../exceptions/FormError';
import analytics from '@/analytics';

import { logout } from '@/actions/auth';
import { BUILD_ID, getAuthToken } from '@/config/settings';
import { store } from '@/store';
import { AuthorizationError } from './errors';
import trim from 'lodash-es/trim';
import saveFile from './saveFile';
import { nuclearNoCacheHeaders } from '@/ducktape';
import querystring from 'querystring-es3';

let USER_AGENT = '';
/*
try {
  // Build user agent string
  USER_AGENT = '';
} catch (e) {
  e.domain = 'Client.user_agent';
}
*/
// Enable debug output when in Debug mode
const DEBUG_MODE = true;

/**
 * Debug or not to debug
 */
const debug = (title, output) => {
  if (DEBUG_MODE) {
    console.log(title, output);
  }
};

/**
 * Race two promises against each other ... one to fetch a request to & from the API
 *                                          and one for a timeout of 15 seconds
 * @param method
 * @param url
 * @param settings
 * @param body
 * @returns {Promise.<*>}
 * @throws {*} - If the request has failed, the error will be bubbled up
 */
const fetcher = async (method, url, body, settings = null) => {
  const TIMEOUT_LENGTH = 180000; // 3 minutes
  const token = settings?.token ?? getAuthToken();

  let timeoutPromise = new Promise((resolve, reject) =>
    setTimeout(
      () => reject(new Error('Your request timed out.  Please try again.')),
      TIMEOUT_LENGTH
    )
  );

  let fetchPromise = new Promise((resolve, reject) => {
    if (!method) {
      throw new TypeError('Invalid Request Method');
    }

    if (!url) {
      throw new TypeError('Invalid Request Endpoint');
    }

    // Increment module request counter
    Client.requestCounter += 1;

    const request = buildFetchRequest(method, url, body, token, settings);
    debug(`API Request #${Client.requestCounter}`, request);

    // Make the request
    return fetch(request)
      .then(checkResponseIsValid)
      .then(handleValidResponse(Client.requestCounter, settings))
      .then(checkResponseForErrors)
      .then(responseBody => resolve(responseBody))
      .catch(e => {
        if (e instanceof AuthorizationError) {
          store.dispatch(logout());
        }
        e.domain = 'Client.fetcher.fetch';
        e.data = { url, body };
        e.code = e.status_code;
        analytics.trackError(e);
        reject(e);
      });
  });

  return Promise.race([fetchPromise, timeoutPromise]);
};

/**
 * Replace matching params in API routes and returns a Request object
 * @param url
 * @param params
 * @returns {Request}
 * @see ../constants/api.js
 */
const buildFetchRequest = (method, url, data, token, settings) => {
  const extraHeaders = settings?.headers ? settings.headers : {};

  const headerValues = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'User-Agent': USER_AGENT,
    Version: BUILD_ID,
    Source: 'EediFamily',
    'X-Is-Trackable': 'true',
    ...extraHeaders,
    ...nuclearNoCacheHeaders
  };

  const isFormData =
    headerValues['Content-Type'] === 'multipart/form-data' ||
    extraHeaders.isFormData;

  if (isFormData) {
    delete headerValues.Accept;
    delete headerValues['Content-Type'];
  }

  const headers = new Headers(headerValues);

  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
  }

  let req = {
    method: method.toUpperCase(),
    headers
  };

  // If this is a get request append data on to the query string
  // https://www.linkedin.com/pulse/how-migrate-from-querystring-urlsearchparams-nodejs-vladim%C3%ADr-gorej/?trk=articles_directory
  if (data) {
    if (method === 'GET') {
      url += '?' + querystring.stringify(data);
    } else if (isFormData) {
      req['body'] = data;
    } else {
      req['body'] = JSON.stringify(data);
    }
  }

  return new Request(url, req);
};

/**
 * @param response
 * @returns {*}
 */
const checkResponseIsValid = response => {
  if (response?.status === 401) {
    throw new AuthorizationError(
      'Authorisation has been denied for this request.'
    );
  }

  return response;
};

const handleValidResponse =
  (requestCounterId, settings = undefined) =>
  response => {
    try {
      if (response.status === 204) {
        return {};
      }

      // This can occur if the API return an OK result with no content
      // TODO - not sure if an empty object is the best thing to return
      // depending on he assumptions made about the request...
      if (!response?.headers?.get('content-type')) return {};

      switch (settings?.readBodyAs) {
        case 'text':
          return response.text();
        case 'blob':
          return response.blob();
        case 'formData':
          return response.formData();
        case 'arrayBuffer':
          return response.arrayBuffer();
        case 'file':
          return saveResponseAsFile(response);

        case 'json':
          return response.json();

        case 'response':
          return response;

        case 'none':
          return;

        default:
          return response.json();
      }
    } catch (e) {
      analytics.trackError(e);
      console.error(`Api Request #${requestCounterId}`, e);
      throw new SyntaxError('Improper Response Received');
    }
  };

// If the response has an error or a message then throw an error
const checkResponseForErrors = response => {
  if (!response) return;

  const error = response.message || response.error;
  if (error) {
    if (response.modelState) {
      throw new FormError(error, response.modelState);
    }

    throw new Error(error);
  }

  return response;
};

const saveResponseAsFile = async response => {
  const header = response.headers.get('content-disposition') ?? '';

  const parts = header.split(';');

  const fileName = parts
    .find(part => part.includes('filename='))
    ?.split('=')[1];

  if (!response.ok || (!header && !fileName)) {
    throw new Error('Error downloading file');
  }

  // Filename may include double quotes for some reason
  const cleanedFileName = trim(fileName, '  "');

  saveFile(await response.blob(), cleanedFileName);
};

const Client = {
  requestCounter: 0,
  /**
   * @param url
   * @param params - TODO: Rename to body
   * @param settings
   * @returns {Promise.<*>}
   * @throws {*} - If the request has failed, the error will be bubbled up
   */
  get: (url, params, settings = null) => fetcher('GET', url, params, settings),
  /**
   * @param url
   * @param params - TODO: Rename to body
   * @param settings
   * @returns {Promise.<*>}
   * @throws {*} - If the request has failed, the error will be bubbled up
   */
  post: (url, params, settings = null) =>
    fetcher('POST', url, params, settings),
  /**
   * @param url
   * @param params - TODO: Rename to body
   * @param settings
   * @returns {Promise.<*>}
   * @throws {*} - If the request has failed, the error will be bubbled up
   */
  patch: (url, params, settings = null) =>
    fetcher('PATCH', url, params, settings),
  /**
   * @param url
   * @param params - TODO: Rename to body
   * @param settings
   * @returns {Promise.<*>}
   * @throws {*} - If the request has failed, the error will be bubbled up
   */
  put: (url, params, settings = null) => fetcher('PUT', url, params, settings),
  /**
   * @param url
   * @param params - TODO: Rename to body
   * @param settings
   * @returns {Promise.<*>}
   * @throws {*} - If the request has failed, the error will be bubbled up
   */
  delete: (url, params, settings = null) =>
    fetcher('DELETE', url, params, settings)
};

export class FormError extends Error {
  constructor(message, modelState) {
    super(message);

    let formattedModelState = {};

    for (const [key, error] of Object.entries(modelState)) {
      if (error) {
        formattedModelState[key] = { message: error };
      }
    }
    this.modelState = formattedModelState;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FormError);
    }
  }
}

export default Client;
